import { DatePipe, NgClass } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, effect, inject, input, OnInit, output, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { catchError, firstValueFrom, forkJoin, of } from 'rxjs';

import { AuthService } from '../../../core/auth/auth.service';
import { TranslationService } from '../../../core/i18n/translation.service';
import { UserAdminApi } from '../../users/services/user-admin.api';
import {
  IssueAttachmentDto,
  IssueDto,
  IssueLocationDto,
  IssuePriority,
  IssueRoleDto,
} from '../models/issue.models';
import { IssuesApi } from '../services/issues.api';
import { IssueDecisionModalComponent } from './issue-decision-modal.component';

@Component({
  selector: 'app-issue-editor',
  imports: [DatePipe, IssueDecisionModalComponent, NgClass, ReactiveFormsModule],
  templateUrl: './issue-editor.component.html',
  styleUrl: './issue-editor.component.css',
})
export class IssueEditorComponent implements OnInit {
  private readonly api = inject(IssuesApi);
  private readonly authService = inject(AuthService);
  private readonly usersApi = inject(UserAdminApi);
  private readonly formBuilder = inject(FormBuilder);

  protected readonly i18n = inject(TranslationService);
  readonly issue = input.required<IssueDto>();
  readonly cancelRequested = output<void>();
  readonly saved = output<IssueDto>();
  readonly decisionUpdated = output<IssueDto>();

  protected readonly form = this.formBuilder.nonNullable.group({
    title: ['', [Validators.required, Validators.maxLength(255)]],
    description: ['', Validators.required],
    priority: ['medium' as IssuePriority, Validators.required],
    dueDate: [''],
    locationId: ['', Validators.required],
    assignedRoleId: ['', Validators.required],
  });
  protected readonly locations = signal<IssueLocationDto[]>([]);
  protected readonly roles = signal<IssueRoleDto[]>([]);
  protected readonly existingAttachments = signal<IssueAttachmentDto[]>([]);
  protected readonly newAttachments = signal<File[]>([]);
  protected readonly currentIssue = signal<IssueDto | null>(null);
  protected readonly reporterName = signal('');
  protected readonly displayedIssue = computed(() => this.currentIssue() ?? this.issue());
  protected readonly isDecisionOpen = signal(false);
  private readonly removedAttachmentIds = signal<string[]>([]);
  protected readonly isLoadingOptions = signal(true);
  protected readonly isSaving = signal(false);
  protected readonly optionsLoadError = signal('');
  protected readonly attachmentError = signal('');
  protected readonly saveError = signal('');
  protected readonly priorities: Array<{ value: IssuePriority; labelKey: string }> = [
    { value: 'low', labelKey: 'issueReport.priority.low' },
    { value: 'medium', labelKey: 'issueReport.priority.medium' },
    { value: 'critical', labelKey: 'issueReport.priority.critical' },
  ];

  constructor() {
    effect(() => {
      const issue = this.issue();
      this.currentIssue.set(issue);
      this.form.reset({
        title: issue.title,
        description: issue.description,
        priority: issue.priority,
        dueDate: this.toDateTimeLocal(issue.due_date),
        locationId: issue.location_id,
        assignedRoleId: issue.assigned_role_id === null ? '' : String(issue.assigned_role_id),
      });
      this.existingAttachments.set([...issue.attachments]);
      this.newAttachments.set([]);
      this.removedAttachmentIds.set([]);
      this.attachmentError.set('');
      this.saveError.set('');
    });
  }

  ngOnInit(): void {
    void this.loadOptions();
  }

  protected getStatusLabel(): string {
    return this.i18n.t(`issueList.status.${this.displayedIssue().status}`);
  }

  protected getStatusClass(): Record<string, boolean> {
    const status = this.displayedIssue().status;
    return {
      'app-status-chip--draft': status === 'new',
      'issue-editor__status--in-progress': status === 'in_progress',
      'app-status-chip--active': status === 'resolved',
      'issue-editor__status--rejected': status === 'rejected',
      'app-status-chip--archived': status === 'archived',
    };
  }

  protected openDecision(): void {
    if (!this.isSaving()) {
      this.isDecisionOpen.set(true);
    }
  }

  protected closeDecision(): void {
    this.isDecisionOpen.set(false);
  }

  protected handleDecision(updatedIssue: IssueDto): void {
    this.currentIssue.set(updatedIssue);
    this.isDecisionOpen.set(false);
    this.decisionUpdated.emit(updatedIssue);
  }

  protected cancel(): void {
    if (!this.isSaving()) {
      this.cancelRequested.emit();
    }
  }

  protected addAttachments(event: Event): void {
    const input = event.target as HTMLInputElement;
    const selectedFiles = Array.from(input.files ?? []);
    input.value = '';

    const images = selectedFiles.filter(({ type }) => type.startsWith('image/'));
    this.attachmentError.set(
      images.length === selectedFiles.length
        ? ''
        : this.i18n.t('issueEditor.errors.invalidAttachment'),
    );

    if (images.length) {
      this.newAttachments.update((current) => [...current, ...images]);
    }
  }

  protected removeExistingAttachment(attachment: IssueAttachmentDto): void {
    this.existingAttachments.update((current) => current.filter(({ id }) => id !== attachment.id));
    this.removedAttachmentIds.update((current) => [...current, attachment.id]);
    this.attachmentError.set('');
  }

  protected removeNewAttachment(index: number): void {
    this.newAttachments.update((current) => current.filter((_, itemIndex) => itemIndex !== index));
    this.attachmentError.set('');
  }

  protected async save(): Promise<void> {
    this.form.markAllAsTouched();
    this.saveError.set('');

    const value = this.form.getRawValue();
    if (!value.title.trim()) {
      this.form.controls.title.setErrors({ required: true });
    }
    if (!value.description.trim()) {
      this.form.controls.description.setErrors({ required: true });
    }
    if (this.form.invalid || this.isLoadingOptions() || this.optionsLoadError()) {
      return;
    }

    const assignedRoleId = Number(value.assignedRoleId);
    if (!Number.isInteger(assignedRoleId) || assignedRoleId < 1) {
      this.form.controls.assignedRoleId.setErrors({ required: true });
      return;
    }

    this.isSaving.set(true);
    try {
      const hasValidSession = await this.authService.ensureValidSession();
      if (!hasValidSession) {
        this.saveError.set(this.i18n.t('issueEditor.errors.sessionExpired'));
        return;
      }

      let updatedIssue: IssueDto;
      try {
        updatedIssue = await firstValueFrom(
          this.api.updateIssue(this.issue().id, {
            title: value.title.trim(),
            description: value.description.trim(),
            priority: value.priority,
            dueDate: value.dueDate ? new Date(value.dueDate).toISOString() : null,
            locationId: value.locationId,
            assignedRoleId,
          }),
        );
      } catch (error) {
        this.setOperationError(error, 'issueEditor.errors.save');
        return;
      }

      for (const attachment of [...this.newAttachments()]) {
        try {
          const createdAttachment = await firstValueFrom(
            this.api.addIssueAttachment(this.issue().id, attachment),
          );
          this.newAttachments.update((current) =>
            current.filter((currentAttachment) => currentAttachment !== attachment),
          );
          this.existingAttachments.update((current) => [...current, createdAttachment]);
        } catch (error) {
          this.setOperationError(error, 'issueEditor.errors.uploadAttachment');
          return;
        }
      }

      for (const attachmentId of [...this.removedAttachmentIds()]) {
        try {
          await firstValueFrom(this.api.deleteIssueAttachment(this.issue().id, attachmentId));
          this.removedAttachmentIds.update((current) =>
            current.filter((currentId) => currentId !== attachmentId),
          );
        } catch (error) {
          this.setOperationError(error, 'issueEditor.errors.deleteAttachment');
          return;
        }
      }

      this.saved.emit({
        ...updatedIssue,
        attachments: this.existingAttachments(),
      });
    } finally {
      this.isSaving.set(false);
    }
  }

  private async loadOptions(): Promise<void> {
    this.isLoadingOptions.set(true);
    this.optionsLoadError.set('');

    try {
      const response = await firstValueFrom(
        forkJoin({
          locations: this.api.getLocations(),
          roles: this.api.getRoles(),
          reporter: this.usersApi
            .getUser(this.issue().reporter_user_code)
            .pipe(catchError(() => of(null))),
        }),
      );
      this.locations.set(response.locations.results);
      this.roles.set(response.roles.results);
      this.reporterName.set(
        [response.reporter?.first_name ?? '', response.reporter?.last_name ?? '']
          .map((part) => part.trim())
          .filter(Boolean)
          .join(' '),
      );
    } catch {
      this.optionsLoadError.set(this.i18n.t('issueEditor.errors.loadOptions'));
    } finally {
      this.isLoadingOptions.set(false);
    }
  }

  private toDateTimeLocal(value: string | null): string {
    if (!value) {
      return '';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    const pad = (part: number) => String(part).padStart(2, '0');
    return [
      date.getFullYear(),
      '-',
      pad(date.getMonth() + 1),
      '-',
      pad(date.getDate()),
      'T',
      pad(date.getHours()),
      ':',
      pad(date.getMinutes()),
    ].join('');
  }

  private setOperationError(error: unknown, fallbackKey: string): void {
    const key =
      error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403)
        ? 'issueEditor.errors.sessionExpired'
        : fallbackKey;
    this.saveError.set(this.i18n.t(key));
  }
}
